import { defineComponent as _defineComponent } from 'vue'
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';

import { goBack } from '@/composables/useNavigation';
import { NumberService, SelectOptionsService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore, useAuthStore } from '@/stores';
import { applyPhoneMask } from '@/utils/number';

import {
    DefaultServiceResult,
    NumberFormState,
    NumberModel,
    Permission,
    SelectOptionsServiceGetLevelsResult,
} from '@/types';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'NumberFormPage',
  setup(__props, { expose: __expose }) {
  __expose();

const formState = reactive<NumberFormState>({
    levelId: undefined,
    connectFlowId: undefined,
    mediaLabel: undefined,
});
const number = ref<NumberModel>();
const levelOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const connectFlowOptions: Ref<Array<{ id: number; name: string }>> = ref([]);
const receiveWhatsappCode = ref(false);
const isInitialLoad = ref(true);

const rules = computed(() => ({
    levelId: { required },
    connectFlowId: {},
    mediaLabel: { maxLength: maxLength(250) },
}));

const route = useRoute();
const uiStore = useUiStore();
const authStore = useAuthStore();
const v$: Ref<Validation<ValidationArgs<NumberFormState>>> = useVuelidate(rules, formState);
const canAssignWhatsappCode = computed(() =>
    authStore.userPermissions.includes(Permission.ASSIGN_WHATSAPP_CODE_NUMBER),
);

watch(
    () => formState.levelId,
    async (newVal, oldVal) => {
        if (!isInitialLoad.value && newVal !== oldVal) {
            formState.connectFlowId = null;
            receiveWhatsappCode.value = false;
        }
        await fetchConnectFlows();
    },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchNumber = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await NumberService.get<NumberModel>(id);
        number.value = data;
        formState.levelId = data.level.id;
        receiveWhatsappCode.value = data.whatsappRouting?.isActive === true;
        formState.connectFlowId = data.whatsappRouting?.isActive ? null : data.connectFlowId;
        formState.mediaLabel = data.mediaLabel;
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

const fetchLevels = async () => {
    try {
        const { data } = await SelectOptionsService.getLevels();
        levelOptions.value = data;
    } catch (err) {
        toastServiceError(err);
    }
};

const fetchConnectFlows = async () => {
    if (formState.levelId) {
        try {
            const { data } = await SelectOptionsService.getConnectFlows({
                params: {
                    level: formState.levelId,
                },
            });
            connectFlowOptions.value = [...data];
        } catch (err) {
            toastServiceError(err);
        }
    }
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    if (formState.mediaLabel === '') {
        formState.mediaLabel = null;
    }

    try {
        setLoading(true);
        const id = number.value?.id as number;

        if (receiveWhatsappCode.value) {
            await WhatsappCodeService.activateRouting(id);
            const { levelId, mediaLabel } = formState;
            const { data } = await NumberService.update<DefaultServiceResult>(id, {
                levelId,
                mediaLabel,
            });
            toast.success(data.message);
        } else {
            const { data } = await NumberService.update<DefaultServiceResult>(id, formState);
            toast.success(data.message);
        }

        v$.value.$reset();
        goBack({ name: 'ViewNumberById', params: { id } });
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchNumber(Number(route.params.id));
    await fetchLevels();

    if (formState.levelId) {
        await fetchConnectFlows();
    }

    isInitialLoad.value = false;
});

const __returned__ = { formState, number, levelOptions, connectFlowOptions, receiveWhatsappCode, isInitialLoad, rules, route, uiStore, authStore, v$, canAssignWhatsappCode, setLoading, fetchNumber, fetchLevels, fetchConnectFlows, onSubmit, get QuestionMarkCircleIcon() { return QuestionMarkCircleIcon }, get applyPhoneMask() { return applyPhoneMask }, get Permission() { return Permission }, SVGWhatsApp }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})